import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserData, loginUser, logoutUser, registerUser, resetPassword } from '../firebase/auth';
import orgAccessControlService from '../services/orgAccessControlService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  const [cmtIssues, setCmtIssues] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          console.log('Auth state changed - User authenticated:', {
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            isAnonymous: user.isAnonymous,
            metadata: user.metadata
          });

          // First get the user data
          const userData = await getUserData(user.uid);
          console.log('DEBUG - User data from Firestore:', userData);
          
          // Verify we have the required data
          if (!userData || !userData.orgId) {
            console.error('User data missing required fields:', { userData });
            setCurrentUser(null);
            setAccessDenied({
              reason: 'INVALID_USER_DATA',
              message: 'Your account is not properly configured. Please contact support.',
              shouldLogout: true
            });
            setLoading(false);
            return;
          }

          // Validate complete access (organization + user + CMT)
          console.log('🔒 About to validate access for:', { userId: user.uid, orgId: userData.orgId });
          const accessValidation = await orgAccessControlService.validateCompleteAccess(user.uid, userData.orgId);
          console.log('🔒 Access validation result:', accessValidation);
          
          if (!accessValidation.hasAccess) {
            console.error('Access denied:', accessValidation);
            setCurrentUser(null);
            setAccessDenied(accessValidation);
            
            if (accessValidation.shouldLogout) {
              await orgAccessControlService.forceLogout(accessValidation.reason, user.uid, userData.orgId);
            }
            
            setLoading(false);
            return;
          }

          // Set CMT issues if any (non-blocking)
          if (accessValidation.cmtIssues) {
            setCmtIssues(accessValidation.cmtIssues);
          } else {
            setCmtIssues(null);
          }

          console.log('User data loaded successfully:', {
            uid: user.uid,
            orgId: userData.orgId,
            role: userData.role,
            isAdmin: userData.isAdmin,
            isSuperAdmin: userData.superAdmin === true
          });

          // Set the user with complete data including access validation results
          const completeUserData = {
            ...user,
            ...userData,
            ...accessValidation.user, // Include validated user data
            isLoaded: true, // Add a flag to indicate data is fully loaded
            isAdmin: userData.role === 'admin' || userData.isAdmin || userData.superAdmin === true,
            isSuperAdmin: userData.superAdmin === true,
            organizationData: accessValidation.organization,
            requiresSetup: accessValidation.requiresSetup
          };

          console.log('Setting current user with complete data:', {
            uid: completeUserData.uid,
            email: completeUserData.email,
            orgId: completeUserData.orgId,
            role: completeUserData.role,
            isLoaded: completeUserData.isLoaded,
            isAdmin: completeUserData.isAdmin,
            isSuperAdmin: completeUserData.isSuperAdmin,
            rawUserData: completeUserData
          });

          setCurrentUser(completeUserData);
          
          // 🔔 LOGIN TRIGGER: Show all outstanding tasks as notifications (for auto-login on page load)
          setTimeout(() => {
            if (window.showAllOutstandingTasks) {
              console.log('🔔 AuthContext: Triggering task notifications on auth state change (page load)');
              window.showAllOutstandingTasks();
            }
          }, 1500); // Slightly longer delay for page load scenario
        } else {
          console.log('Auth state changed - No user');
          setCurrentUser(null);
          // Don't clear accessDenied here - let it persist to show error messages
          // setAccessDenied(null);
          setCmtIssues(null);
        }
      } catch (error) {
        console.error('🚨 CRITICAL AUTH ERROR:', error);
        console.error('🚨 Error details:', { 
          message: error.message, 
          stack: error.stack,
          name: error.name 
        });
        setCurrentUser(null);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setAccessDenied(null);
      setLoading(true);
      
      const { user, userData } = await loginUser(email, password);
      
      // Validate access after successful authentication
      const accessValidation = await orgAccessControlService.validateCompleteAccess(user.uid, userData.orgId);
      
      if (!accessValidation.hasAccess) {
        console.error('Login access denied:', accessValidation);
        setAccessDenied(accessValidation);
        
        if (accessValidation.shouldLogout) {
          await orgAccessControlService.forceLogout(accessValidation.reason, user.uid, userData.orgId);
        }
        
        throw new Error(accessValidation.message);
      }

      // Set CMT issues if any (non-blocking)
      if (accessValidation.cmtIssues) {
        setCmtIssues(accessValidation.cmtIssues);
      } else {
        setCmtIssues(null);
      }
      
      const completeUserData = {
        ...user,
        ...userData,
        ...accessValidation.user,
        isLoaded: true,
        isAdmin: userData.role === 'admin' || userData.isAdmin || userData.superAdmin === true,
        isSuperAdmin: userData.superAdmin === true,
        organizationData: accessValidation.organization,
        requiresSetup: accessValidation.requiresSetup
      };
      
      setCurrentUser(completeUserData);
      
      // 🔔 LOGIN TRIGGER: Show all outstanding tasks as notifications
      setTimeout(() => {
        if (window.showAllOutstandingTasks) {
          console.log('🔔 AuthContext: Triggering task notifications on login');
          window.showAllOutstandingTasks();
        }
      }, 1000); // Small delay to ensure Layout component has loaded
      
      return completeUserData;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      setAccessDenied(null);
      setCmtIssues(null);
      setLoading(true);
      await logoutUser();
      setCurrentUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, userData) => {
    try {
      setError(null);
      setLoading(true);
      const user = await registerUser(email, password, userData);
      setCurrentUser(user);
      return user;
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetUserPassword = async (email) => {
    try {
      setError(null);
      setLoading(true);
      await resetPassword(email);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    accessDenied,
    cmtIssues,
    login,
    logout,
    register,
    resetUserPassword,
    setCurrentUser,
    setCmtIssues
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 